import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ServerTableActions, ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ServerTable } from 'shared/ui/ServerTable';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { ContractDetailsDrawer, contractDetailsDrawerActions } from 'widgets/ContractDetailsDrawer';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { ExportEntity, ExportModal, getSelfIssuedReceiptColumnKeys, useOpenExportModal } from 'features/ExportModal';
import { useTableFilterContext } from 'features/TableFilter';
import { ReactComponent as ExportIcon } from 'shared/assets/icons/ExportIcon.svg';
import { useGetCurrencySymbol } from 'app/appState';
import { SelfIssuedReceiptsListFilter } from './SelfIssuedReceiptsListFilter';
import { transformSelfIssuedReceiptsListFilters } from '../utils/transformSelfIssuedReceiptsListFilters';
import classNames from 'classnames';
import { ReactComponent as DocumentIcon } from 'shared/assets/icons/DocumentIcon.svg';
import { getUserName } from 'entities/User';
import { generatRefundPdfLink, Refund, useGetPaginatedSelfIssuedReceiptsQuery } from 'entities/Payment';
import { roundNumber } from 'shared/utils/helpers/roundNumber';

export const SelfIssuedReceiptsTable: FC = memo(() => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const currencySymbol = useGetCurrencySymbol();

  const { openExportModal, isExportEnabled } = useOpenExportModal();

  const { t } = useAppTranslation(['contracts', 'common']);

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const goToUserContracts = useCallback(
    (userId: string): void => {
      navigate(`${AppRoutes.USERS}/${userId}/contracts`);
    },
    [navigate],
  );

  const openContractDetails = useCallback(
    (contractId: string): void => {
      dispatch(contractDetailsDrawerActions.setOpenContractDetails(contractId));
    },
    [dispatch],
  );

  const columns = useMemo<ColumnsType<Refund>>(
    () => [
      {
        title: t('Document number'),
        key: 'documentNumber',
        render: (_, record) => <div className="text-primary font-normal">#{record.documentNumber}</div>,
      },
      {
        title: t('Warehouse'),
        key: 'warehouse',
        render: (_, record) => {
          return <div className="text-primary font-normal">{record.payment.invoice.warehouse?.name}</div>;
        },
      },
      {
        title: t('Customer'),
        key: 'user',
        render: (_, record) => (
          <div
            className="text-accent font-semibold underline cursor-pointer"
            onClick={() => {
              goToUserContracts(record.payment.user.userId);
            }}
          >
            {getUserName(record.payment.user)}
          </div>
        ),
      },

      {
        title: t('Contract number'),
        key: 'contractNumber',
        render: (_, record) => {
          return (
            <div
              className="text-accent underline cursor-pointer"
              onClick={() => {
                if (record.payment.invoice.contract?.contractId) {
                  openContractDetails(record.payment.invoice.contract.contractId);
                }
              }}
            >
              #{record.payment.invoice.contract?.contractNumber}
            </div>
          );
        },
      },
      {
        title: t('Amount'),
        key: 'amount',
        render: (_, record) => {
          return (
            <div className="text-primary font-normal">
              {record.amount} {currencySymbol}
            </div>
          );
        },
      },
      {
        title: t('VAT'),
        key: 'vatAmount',
        render: (_, record) => {
          const totalVatAmount = roundNumber(record.refundItems.reduce((acc, item) => acc + item.vatRateAmount, 0));
          return (
            <div className="text-primary font-normal">
              {totalVatAmount} {currencySymbol}
            </div>
          );
        },
      },
      {
        title: t('Date'),
        key: 'createdAt',
        render: (_, record) => <div className="text-primary font-normal">{getClientDateFormat(record.createdAt)}</div>,
      },
      {
        title: t('Refunded by'),
        key: 'refundedBy',
        render: (_, record) => <div className="text-primary font-normal">{getUserName(record.payment.refundedBy)}</div>,
      },
    ],
    [t, goToUserContracts, openContractDetails, currencySymbol],
  );

  const rowActions: Array<ServerTableRowActions<Refund>> = useMemo(
    () => [
      {
        name: 'downloadRefund',
        theme: 'clear',
        description: t('Download', { ns: 'common' }),
        icon: <DocumentIcon className="[&>path]:stroke-primary" />,
        onClick: (selectedRefund) => {
          const pdfLink = generatRefundPdfLink(selectedRefund.refundId);

          window.open(pdfLink);
        },
      },
    ],
    [t],
  );

  const tableActions: Array<ServerTableActions<Refund>> = useMemo(() => {
    const exportAction: Array<ServerTableActions<Refund>> = isExportEnabled
      ? [
          {
            name: t('Export', { ns: 'common' }),
            theme: 'secondary',
            icon: <ExportIcon />,
            iconPosition: 'prev',
            onClick: () => {
              openExportModal({
                filters: transformSelfIssuedReceiptsListFilters(appliedFilters),
                columns: getSelfIssuedReceiptColumnKeys(t),
                entity: ExportEntity.SELF_ISSUED_RECEIPT,
              });
            },
          },
        ]
      : [];

    return [
      ...exportAction,
      {
        name: t('Filters', { ns: 'common' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ];
  }, [appliedFilters, isExportEnabled, openExportModal, setFiltersOpened, t]);

  const highlightRow = (record: Refund): string => {
    return classNames({
      'bg-secondary': !record.payment.invoice.contract?.isApplicable,
    });
  };

  return (
    <div className="pt-4">
      <div className="font-semibold text-3xl mb-7">{t('Self issued receipts')}</div>
      <ServerTable
        columns={columns}
        fetchData={useGetPaginatedSelfIssuedReceiptsQuery}
        rowKey="refundId"
        search
        tableActionsPosition="prev"
        searchPlaceholder={t('Enter customer name or receipt | contract number')}
        tableActions={tableActions}
        tags={tags}
        onAllFiltersClear={clearAllFilters}
        defaultFilters={transformSelfIssuedReceiptsListFilters(appliedFilters)}
        rowClassName={highlightRow}
        rowActions={rowActions}
      />
      <ExportModal />
      <ContractDetailsDrawer />
      <SelfIssuedReceiptsListFilter />
    </div>
  );
});
